import { useRef, type CSSProperties } from "react";
import { displayName, hasJoined } from "../../../../engine/selectors";
import type { Player, PlayerId, TournamentState } from "../../../../engine/types";
import { Face } from "../../components/Face";
import { FitText } from "../../components/FitText";
import { isFavourite } from "../../lib/mascot";
import { Qr } from "./qr";

/** The grid's box is about this much wider than it is tall; a tile is this much taller than wide. */
const BOX = 1.85;
const TILE = 1.3;

/** The number of columns that gives `n` tiles the biggest faces, in a box `box` times wider than tall. */
export function columnsFor(n: number, box = BOX): number {
  let best = 1;
  let size = 0;
  for (let cols = 1; cols <= n; cols += 1) {
    const rows = Math.ceil(n / cols);
    const cell = Math.min(box / cols, 1 / (rows * TILE));
    if (cell > size + 1e-9) {
      size = cell;
      best = cols;
    }
  }
  return best;
}

/** In the hall: tapped I'm here (or marked here by an admin), or here and on a break. */
export function isHere(p: Player): boolean {
  return p.status === "in" || p.status === "break";
}

/**
 * Before round 1: everyone who is here (a face and the name they picked), the big code to scan,
 * and who is not here yet. Joining at home from the group chat is not being here: those names
 * are asked to tap I'm here, apart from those still to join. A face pops in at the end as its
 * player arrives while the wall is up.
 */
export function JoinWall({ s, link, faceOf }: { s: TournamentState; link: string | null; faceOf: (p: Player | undefined) => string | null }) {
  // the name lists in name order, so a name is found at a glance (the faces keep arrival order)
  const everyone = Object.values(s.players).filter((p) => p.status !== "left").sort((x, y) => x.name.localeCompare(y.name, "en"));
  const here = everyone.filter(isHere);
  const tapHere = everyone.filter((p) => !isHere(p) && hasJoined(p));
  const toCome = everyone.filter((p) => !isHere(p) && !hasJoined(p));

  // The state does not say when somebody joined, so the wall keeps the order it saw them in.
  const order = useRef<PlayerId[] | null>(null);
  const fresh = useRef(new Set<PlayerId>());
  const first = order.current === null;
  const known = order.current ?? [];
  for (const p of here) {
    if (known.includes(p.id)) continue;
    known.push(p.id);
    if (!first) fresh.current.add(p.id);
  }
  order.current = known;
  const tiles = known.map((id) => s.players[id]).filter((p): p is Player => !!p && isHere(p));

  const cols = columnsFor(Math.max(tiles.length, 1));
  const rows = Math.ceil(Math.max(tiles.length, 1) / cols);
  return (
    <>
      <main className="proj-join">
        <div className="join-grid">
          <div className="join-cells" style={{ "--cols": cols, "--rows": rows } as CSSProperties}>
            {tiles.map((p, i) => {
              const name = displayName(p, "wall");
              return (
                <div key={p.id} className={fresh.current.has(p.id) ? "join-tile fresh" : "join-tile"} data-court={(i % 4) + 1}>
                  <Face name={name} src={faceOf(p)} crowned={isFavourite(p.id)} />
                  <div className="join-plate">
                    <FitText className="join-name wall-display">{name}</FitText>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <aside className="join-scan">
          {link ? <Qr link={link} className="join-qr" /> : null}
          <p className="join-how wall-display">Scan the code, then tap I'm here</p>
        </aside>
      </main>
      <footer className="join-foot">
        {tapHere.length ? (
          <div className="join-wait">
            <h2>Tap I'm here</h2>
            <FitText className="join-names">{tapHere.map((p) => displayName(p, "wall")).join(", ")}</FitText>
          </div>
        ) : null}
        {toCome.length || !tapHere.length ? (
          <div className="join-wait">
            <h2>{toCome.length ? "Still to come" : "Everyone is here"}</h2>
            {toCome.length ? <FitText className="join-names">{toCome.map((p) => displayName(p, "wall")).join(", ")}</FitText> : null}
          </div>
        ) : null}
      </footer>
    </>
  );
}
